/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    object, string, shape, bool,
} from 'prop-types';
import Media from 'react-media';
import mbpLogger from 'mbp-logger';
import { useQuery } from 'react-apollo';
import ReactHTMLParser from 'react-html-parser';
import { Container, Grid, makeStyles } from '@material-ui/core';
import Graphql404ErrorProductContainer from './Graphql404ErrorProductContainer';
import { GRAPHQL_ENV } from '../../../../../gql';
import { trackData } from '../../../../../helpers/tracking/common/commonTrackingHelpers';
import { getProductDetailBySKU } from '../../../../../pages/Account/services/graphQL';
import GraphqlSeoHeadTags from '../../../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
// import { setLocale } from '../../../../../../state/ducks/Member/ducks/Auth/helper/helper';
import ProductNotAvailablePage from './ProductNotAvailablePage';

const useStyles = makeStyles((theme) => ({
    header: {
        WebkitFontSmoothing: 'antialiased',
        textAlign: 'center',
        width: '100%',
    },
    oopsContainer: {
        lineHeight: '1.2',
        borderTop: `1px solid ${theme.palette.colorNeutral20}`,
        padding: '20px 0 20px 0',
        '& h3': {
            margin: '0 0 5px 0',
            fontSize: '35px',
            letterSpacing: '1.5px',
            fontWeight: 'normal',
            fontFamily: theme.typography.secondaryFontFamily,
        },
    },
    oopsText: {
        color: theme.palette.colorNeutral70,
        fontFamily: theme.typography.fontFamily,
        fontSize: '16px',
        letterSpacing: '.03em',
        textShadow: '1px 1px 1px rgba(0, 0, 0, 0.004)',
    },
    break: {
        height: '12px',
        background: theme.palette.break,
        boxShadow: 'inset 0 1px 3px rgba(59,33,81,0.21), inset 0 1px 2px rgba(59,33,81,0.21)',
    },
    bestSellerContainer: {
        padding: '15px',
    },
    bestSellersText: {
        color: theme.palette.black,
        fontFamily: theme.typography.secondaryFontFamily,
        fontSize: '22px',
        letterSpacing: '1.2px',
    },
}));

const ProductNotAvailableContainer = ({
    code, productUnavailableName, brand, seoData,
}) => {
    const classes = useStyles();
    trackData({
        action: 'track_404_data',
    });

    const SOLD_OUT_PRODUCT_QUERY = getProductDetailBySKU(code, brand['domain-name'], GRAPHQL_ENV);
    const { data, error, loading } = useQuery(SOLD_OUT_PRODUCT_QUERY);
    if (loading) {
        return (
            <Media
                query="(min-width: 599px)"
            >
                {(matches) => (matches
                    ? <div>PDP Skeleton</div>
                    : <div>Mobile PDP Skeleton</div>
                )}
            </Media>
        );
    }
    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: SOLD_OUT_PRODUCT_QUERY,
            component: 'ProductNotAvailableContainer.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            error,
        });
    }

    if (typeof window !== 'undefined' && window.is404) {
        window.is404 = false;
    }

    if (data?.findProductByPartNumber) {
        return (
            <ProductNotAvailablePage
                data={data?.findProductByPartNumber}
                brand={brand}
                seoData={seoData}
            />
        );
    }

    return (
        <Container>
            {
                seoData?.seo_searchable
                    ? (
                        <GraphqlSeoHeadTags seoData={seoData} />
                    ) : null
            }
            <Grid className={classes.header}>
                <Grid className={classes.oopsContainer}>
                    {productUnavailableName ? <h3>{ReactHTMLParser(productUnavailableName)}</h3> : null}
                    <div className={classes.oopsText}>We are sorry, the product you have selected is not available. Please select from the list of products below or continue shopping.</div>
                </Grid>
                <div className={classes.break} />
                <Grid className={classes.bestSellerContainer}>
                    <span className={classes.bestSellersText}>Trending Products</span>
                </Grid>
            </Grid>
            <Graphql404ErrorProductContainer brand={brand} eventSubType="productNotAvailable" />;
        </Container>
    );
};

ProductNotAvailableContainer.propTypes = {
    productUnavailableName: string.isRequired,
    brand: object.isRequired,
    code: string.isRequired,
    seoData: shape({
        keywords: string.isRequired,
        meta_description: string.isRequired,
        page_title: string.isRequired,
        seo_searchable: bool.isRequired,
    }).isRequired,
};

export default ProductNotAvailableContainer;
